import * as Sentry from '@sentry/nextjs';

// 회의록 녹음 실패 관측성(PORTAL-12589)
// 녹음/업로드/문서연결 실패를 Sentry로 계측한다. 실패 지점(stage)과
// 브라우저/OS·mimeType·blob size·duration 등 컨텍스트를 함께 태깅해
// 실패율과 실패 지점을 원격 로그로 집계할 수 있게 한다.

export type RecordFailureStage =
  | 'record' // 녹음 자체 실패 (권한/코덱/blob 미생성 등)
  | 'upload' // 오디오 파일 업로드 실패
  | 'attach'; // 문서에 녹음 연결 실패

type RecordFailureContext = {
  docId?: string;
  title?: string;
  mimeType?: string;
  blobSize?: number; // bytes
  duration?: string | number;
  uuid?: string;
  /** 실패 원인 세부 — 예: getUserMedia 의 DOMException.name (PORTAL-12591) */
  reason?: string;
  /** 업로드 시도 횟수 — 자동 재시도 포함 (PORTAL-12581) */
  attempts?: number;
};

// navigator.userAgentData 는 아직 표준 타입에 없어 별도 선언
type NavigatorUAData = { platform?: string };

export const captureRecordError = (
  stage: RecordFailureStage,
  error: unknown,
  context: RecordFailureContext = {},
) => {
  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('feature', 'talkflow-record');
    scope.setTag('record.stage', stage);
    if (context.mimeType) scope.setTag('record.mimeType', context.mimeType);
    // 태그로 올려 원인별(권한 거부/기기 없음/점유 중) 집계가 가능하게 한다
    if (context.reason) scope.setTag('record.reason', context.reason);

    // 브라우저/OS 컨텍스트
    if (typeof navigator !== 'undefined') {
      const uaData = (
        navigator as Navigator & { userAgentData?: NavigatorUAData }
      ).userAgentData;
      scope.setContext('client', {
        userAgent: navigator.userAgent,
        platform: uaData?.platform ?? navigator.platform,
        language: navigator.language,
      });
    }

    // 녹음/업로드 컨텍스트
    scope.setContext('record', {
      docId: context.docId,
      title: context.title,
      mimeType: context.mimeType,
      blobSize: context.blobSize,
      duration: context.duration,
      uuid: context.uuid,
      reason: context.reason,
      attempts: context.attempts,
    });

    // 녹음 자체 실패는 axios 에러가 아니라 Error 인스턴스가 아닐 수 있으므로 보정
    const err =
      error instanceof Error
        ? error
        : new Error(`[record:${stage}] ${String(error)}`);

    Sentry.captureException(err);
  });
};
